import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaSearch, FaPrint } from 'react-icons/fa';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import LeaseApiService from '../../services/apiService';
import { isAgentApiSuccess, unwrapAgentError } from './agentMasterApiHelpers';

const dateOrDash = (v) => {
  if (!v || v === '0') return '--';
  const d = moment(v);
  return d.isValid() && d.year() > 1900 ? d.format('DD-MMM-YYYY') : '--';
};

// Mirrors the legacy "Broker Document Expiry" popup (frmBrokerDoctExpiryList):
// Expired vs Date Range toggle, Search, Print.
const DocumentExpiryModal = ({ show, onClose }) => {
  const { t } = useTranslation();
  const apiService = new LeaseApiService();
  const [mode, setMode] = useState('expired');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const handleSearch = async () => {
    try {
      setLoading(true);
      const body =
        mode === 'expired'
          ? { expiredOnly: true, fromDate: null, toDate: null }
          : {
              expiredOnly: false,
              fromDate: fromDate ? new Date(fromDate).toISOString() : null,
              toDate: toDate ? new Date(toDate).toISOString() : null,
            };
      const res = await apiService.post('AGENT_MASTER_DOC_EXPIRY', body);
      if (isAgentApiSuccess(res?.data)) {
        setRows(res?.data?.data || []);
      } else {
        setRows([]);
        console.error(unwrapAgentError(res?.data));
      }
    } catch (error) {
      console.error('Error fetching agent document expiry list:', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!rows.length) return;
    const doc = new jsPDF('landscape');
    const primaryColor = [110, 81, 153];
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 297, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(t('agentMaster.documentExpiry.title'), 148.5, 15, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    const columns = ['Name', 'Contact No', 'Email Id', 'Salesperson', 'Description', 'Exp Date', 'File Name'];
    const body = rows.map((r) => [
      r?.name ?? r?.Name ?? '-',
      r?.contactNo ?? r?.ContactNo ?? '-',
      r?.emailId ?? r?.EmailId ?? '-',
      r?.salesperson ?? r?.Salesperson ?? '-',
      r?.description ?? r?.Description ?? '-',
      dateOrDash(r?.expDate ?? r?.ExpDate),
      r?.fileName ?? r?.FileName ?? '-',
    ]);
    doc.autoTable({
      startY: 32,
      head: [columns],
      body,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
    });
    doc.save('Agent_Document_Expiry.pdf');
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" backdrop="static" keyboard={false} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('agentMaster.documentExpiry.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex align-items-center gap-4 mb-3 flex-wrap">
          <div className="d-flex align-items-center gap-2">
            <input type="radio" id="docExpExpired" checked={mode === 'expired'} onChange={() => setMode('expired')} />
            <label htmlFor="docExpExpired" className="mb-0">
              {t('agentMaster.documentExpiry.expired')}
            </label>
          </div>
          <div className="d-flex align-items-center gap-2">
            <input type="radio" id="docExpRange" checked={mode === 'range'} onChange={() => setMode('range')} />
            <label htmlFor="docExpRange" className="mb-0">
              {t('agentMaster.documentExpiry.dateRange')}
            </label>
          </div>
          <DatePicker
            selected={fromDate}
            onChange={setFromDate}
            dateFormat="dd-MMM-yyyy"
            className="p-2 form-control"
            disabled={mode !== 'range'}
            placeholderText={t('common.from')}
          />
          <span>{t('common.to')}</span>
          <DatePicker
            selected={toDate}
            onChange={setToDate}
            dateFormat="dd-MMM-yyyy"
            className="p-2 form-control"
            disabled={mode !== 'range'}
            placeholderText={t('common.to')}
          />
          <div className="mainbtn flex-end gap-2" onClick={handleSearch}>
            <FaSearch color="#fff" className="center mt-1 mx-2" />
            <div className="center">{t('common.search')}</div>
          </div>
          <div className="mainbtnGry flex-end gap-2" onClick={handlePrint}>
            <FaPrint color="#fff" className="center mt-1 mx-2" />
            <div className="center">{t('common.print')}</div>
          </div>
        </div>

        <div className="table-container" style={{ overflowX: 'auto', maxHeight: '450px', overflowY: 'auto' }}>
          <table className="table table-bordered gridshad">
            <thead className="dynoTable">
              <tr>
                <th>{t('agentMaster.documentExpiry.columns.name')}</th>
                <th>{t('agentMaster.documentExpiry.columns.contactNo')}</th>
                <th>{t('agentMaster.documentExpiry.columns.emailId')}</th>
                <th>{t('agentMaster.documentExpiry.columns.salesperson')}</th>
                <th>{t('agentMaster.documentExpiry.columns.description')}</th>
                <th>{t('agentMaster.documentExpiry.columns.expDate')}</th>
                <th>{t('agentMaster.documentExpiry.columns.fileName')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center">
                    {t('common.loading')}
                  </td>
                </tr>
              ) : rows.length ? (
                rows.map((r, index) => (
                  <tr key={index}>
                    <td>{r?.name ?? r?.Name ?? '--'}</td>
                    <td>{r?.contactNo ?? r?.ContactNo ?? '--'}</td>
                    <td>{r?.emailId ?? r?.EmailId ?? '--'}</td>
                    <td>{r?.salesperson ?? r?.Salesperson ?? '--'}</td>
                    <td>{r?.description ?? r?.Description ?? '--'}</td>
                    <td>{dateOrDash(r?.expDate ?? r?.ExpDate)}</td>
                    <td>{r?.fileName ?? r?.FileName ?? '--'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center">
                    {t('common.noRecordsFound')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DocumentExpiryModal;
